import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Button } from "@/components/ui/button";
import { useListRiskSummaryFull } from "@/lib/api/risk/summary";
import { get_customer_commodity_top_level, formatPercent } from "./common";
import { ArrowRightIcon } from "lucide-react";
import { ArrowDownIcon } from "lucide-react";
import { formatNumberToDisplay, safeParseDecimal } from "@/lib/api/common";
import { useQueryParamArray } from "@/hooks/use-query-param-array";
import { useSearchParams } from "react-router-dom";
import Decimal from "decimal.js";
import { useQueryParam } from "@/hooks/use-query-param";

interface CustomerCommodityTopLevelStatsRow {
  customer_id: string;
  commodity_type: string;
  commodity_display_name: string;

  acreage: number;
  projected_production: number;
  actual_production: number;

  futuresBreakdown: { label: string; id: string; value: number }[];

  percent_hedged: number;
  total_risk_counts: { production_sold: number, production_unsold: number };
}

export function CustomersTopLevelViewSummary({ baseTopLevelResult }: { baseTopLevelResult: ReturnType<typeof useListRiskSummaryFull> }) {
  const summaryData = baseTopLevelResult?.data?.data?.summary || {};
  const [customerIds, setCustomerIds] = useQueryParamArray("customer_ids");
  const [crop_year] = useQueryParam("crop_year");
  const [searchParams, setSearchParams] = useSearchParams();

  const navigateToDetailView = (customerId: string, commodity: string, detailView: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("customer_ids", customerId);
    newParams.set("commodity", commodity);
    newParams.set("detail_view", detailView);
    setSearchParams(newParams);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const { isReady, customer_commodity_top_level_items, customer_commodity_top_level_columns } = useMemo(() => {
    if (!summaryData?.commodity_list) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };
    if (!summaryData?.customer_name_map) return { isReady: false, customerName: "Loading", commodity_top_level_items: [] };

    const customer_ids = Object.entries(summaryData.customer_name_map).sort((a: any, b: any) => a[1].localeCompare(b[1])).map(([customerId]) => customerId);

    const customer_commodity_top_level_items = [];
    for (const commodity_type of summaryData.commodity_list) {
      for (const customerId of customer_ids) {
        const customer_commodity_top_level_item = get_customer_commodity_top_level({ commodity_type, customer_id: customerId, summaryData });
        if (!customer_commodity_top_level_item) continue;
        if (customer_commodity_top_level_item.acreage === 0) continue;
        customer_commodity_top_level_items.push(customer_commodity_top_level_item);
      }
    }

    const percentSortingFn = (a: any, b: any, columnId: string) => {
      return safeParseDecimal({})(a.getValue(columnId)?.percent || 0).sub(b.getValue(columnId)?.percent || 0).toNumber()
    };

    const computeAggregationFn = (key: string) => (column: any, rows: any) => {
        const base = rows.reduce((acc: any, row: any) => {
          acc.production = acc.production.plus(row.original.production || 0);
          acc.quantity = acc.quantity.plus(row.original[key] || 0);
          return acc;
        }, {
          quantity: new Decimal(0),
          production: new Decimal(0)
        });

      if (base.production.isZero()) {
        return {
          value: base.quantity.toNumber(),
          percent: 0,
        }
      }

      return {
        value: base.quantity.toNumber(),
        percent: safeParseDecimal({})(base.quantity).div(base.production).times(100),
      }
    };

    const computeAggregatedWithFindValue = (findValue: (row: any) => (string | number | undefined | null)) => (column: any, rows: any) => {
      const base = rows.reduce((acc: any, row: any) => {
        acc.production = acc.production.plus(row.original.production || 0);
        acc.quantity = acc.quantity.plus(findValue(row.original) || 0);
        return acc;
      }, { quantity: new Decimal(0), production: new Decimal(0) });

      if (base.production.isZero()) {
        return {
          value: base.quantity.toNumber(),
          percent: 0,
        }
      }

      return {
        value: base.quantity.toNumber(),
        percent: safeParseDecimal({})(base.quantity).div(base.production).times(100),
      }
    };

    const computeAggregatedCell = (info: any) => {
        const item = info.getValue();
        return (
          <span className="text-xs">
              ({item.percent.toFixed(2)}%)
          </span>
        );
    };

    const computeAggreagteAverageNonFalsy = (column: any, rows: any) => {
      const column_key = typeof column === "string" ? column : column.id;

      const base = rows.reduce((acc: any, row: any) => {
        if (!row.original[column_key]) return acc;

        acc.count = acc.count + 1;
        acc.total = acc.total.plus(row.original[column_key] || 0);
        return acc;
      }, { count: 0, total: new Decimal(0) });

      if (base.count === 0) return 0;
      return base.total.div(base.count).toNumber();
    };

    const generateCellLinkDetail = (row: any, detailView: string, value: string | number) => {
      return (
        <button
          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left text-sm"
          onClick={() => navigateToDetailView(row.original.customer_id, row.original.commodity_display_name, detailView)}
        >
          {value}
        </button>
      );
    };


    const customer_commodity_top_level_columns: DataTableColumn<CustomerCommodityTopLevelStatsRow>[] = [
      {
        id: "crop_year",
        header: "Crop Year",
        accessorFn: (_row: any) => crop_year,
        cell: ({ row, getValue }: any) => getValue(),
      },
      {
        id: "commodity_display_name",
        accessorKey: "commodity_display_name",
        header: "Commodity",
        cell: ({ row, getValue }: any) => getValue(),
      },
      {
        id: "customer_id",
        accessorKey: "customer_id",
        header: "Customer",
        accessorFn: (row: any) => { return summaryData.customer_name_map[row.customer_id]; },
        cell: ({ row, getValue }: any) => {
          const customerId = row.original.customer_id;
          const customerName = getValue();
          return (
            <button
              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
              onClick={() => setCustomerIds([customerId])}
            >
              {customerName}
            </button>
          );
        },
      },
      {
        id: "acreage",
        accessorKey: "acreage",
        header: "Acreage",
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(getValue() as number)}
            </span>
          )
        },
        aggregatedCell: ({ row, getValue }: any) => { return formatNumberToDisplay(getValue()); },
      },
      {
        id: "production",
        header: "Production",
        accessorFn: (row: any) => row.actual_production || row.projected_production,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(getValue() as number)}
            </span>
          )
        },
        aggregatedCell: ({ row, getValue }: any) => { return formatNumberToDisplay(getValue()); },
      },
      {
        id: "projected_production",
        accessorKey: "projected_production",
        header: "Projected Production",
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(getValue() as number)}
            </span>
          )
        },
        aggregatedCell: ({ row, getValue }: any) => {
          return formatNumberToDisplay(getValue());
        },
      },
      {
        id: "actual_production",
        accessorKey: "actual_production",
        header: "Actual Production",
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(getValue() as number)}
            </span>
          )
        },
        aggregatedCell: ({ row, getValue }: any) => { return formatNumberToDisplay(getValue()); },
      },
      {
        id: "projected_yield",
        accessorKey: "projected_yield",
        header: "Projected Yield",
        filterFn: "numberFilter" as any,
        aggregationFn: "mean" as any,
        cell: ({ getValue }) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(getValue() as number)}
            </span>
          )
        },
        aggregatedCell: ({ row, getValue }: any) => { return formatNumberToDisplay(getValue()); },
      },
      {
        id: "actual_yield",
        accessorKey: "actual_yield",
        header: "Actual Yield",
        filterFn: "numberFilter" as any,
        aggregationFn: computeAggreagteAverageNonFalsy,
        cell: ({ getValue }) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(getValue() as number)}
            </span>
          )
        },
        aggregatedCell: ({ row, getValue }: any) => { return formatNumberToDisplay(getValue()); },
      },
      {
        id: "total_yield",
        accessorKey: "total_yield",
        header: "Yield",
        filterFn: "numberFilter" as any,
        aggregationFn: "mean" as any,
        cell: ({ getValue }) => {
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(getValue() as number)}
            </span>
          )
        },
        aggregatedCell: ({ row, getValue }: any) => { return formatNumberToDisplay(getValue()); },
      },
      {
        id: "percent_hedged",
        accessorKey: "percent_hedged",
        header: "Hedged %",
        filterFn: "percentageFilter" as any,
        cell: ({ row, getValue }) => {
          const customerId = row.original.customer_id;
          return (
            <button
              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
              onClick={() => setCustomerIds([customerId])}
            >
              {(getValue() as number).toFixed(2)}%
            </button>
          )
        },

        aggregationFn: computeAggregationFn("percent_hedged_quantity"),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
      },
      {
        id: "percent_hedged_w_offer",
        accessorKey: "percent_hedged_w_offer",
        header: "Hedged W Offer",
        filterFn: "percentageFilter" as any,
        accessorFn: (row: any) => row.percentOverProjectedProduction(row.percentHedgedWithBeaconBreakdowns.find((item: { id: string }) => item.id === 'base_with_open_offer')?.value || 0),
        cell: (item) => generateCellLinkDetail(item.row, "", formatNumberToDisplay(item.getValue() as string)),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.percentHedgedWithBeaconBreakdowns.find((item: { id: string }) => item.id === 'base_with_open_offer')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
      },
      {
        id: "percent_physical",
        accessorKey: "percent_physical",
        header: "Physical %",
        filterFn: "percentageFilter" as any,
        cell: ({ row, getValue }) => {
          const customerId = row.original.customer_id;
          return (
            <button
              className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
              onClick={() => setCustomerIds([customerId])}
            >
              {(getValue() as number).toFixed(2)}%
            </button>
          )
        },
        aggregationFn: computeAggregationFn("percent_physical_quantity"),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
      },
      {
        id: "futures_hta_qty",
        header: "HTA Qty",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'hta')?.value || 0,
        cell: (item) => generateCellLinkDetail(item.row, "hta", formatNumberToDisplay(item.getValue() as string)),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'hta')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_hta_pct",
        header: "HTA %",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'hta')?.percent || 0,
        sortingFn: percentSortingFn,
        cell: (item) => generateCellLinkDetail(item.row, "hta", formatNumberToDisplay(item.getValue() as string, { suffix: "%" })),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'hta')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "futures_otc_qty",
        header: "OTC (Futures) Qty",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'otc')?.value || 0,
        filterFn: "numberFilter" as any,
        cell: (item) => generateCellLinkDetail(item.row, "otc-futures", formatNumberToDisplay(item.getValue() as string)),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'otc')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_otc_pct",
        header: "OTC (Futures) %",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'otc')?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: (item) => generateCellLinkDetail(item.row, "otc-futures", formatNumberToDisplay(item.getValue() as string, { suffix: "%" })),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'otc')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "futures_brokerage_qty",
        header: "Brokerage Qty",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'brokerage')?.value || 0,
        filterFn: "numberFilter" as any,
        cell: (item) => generateCellLinkDetail(item.row, "open_brokerage", formatNumberToDisplay(item.getValue() as string)),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'brokerage')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_brokerage_pct",
        header: "Brokerage %",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'brokerage')?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: (item) => generateCellLinkDetail(item.row, "open_brokerage", formatNumberToDisplay(item.getValue() as string, { suffix: "%" })),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'brokerage')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "futures_dpq_qty",
        header: "DPQ (Futures) Qty",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'dpq_futures')?.value || 0,
        filterFn: "numberFilter" as any,
        cell: (item) => generateCellLinkDetail(item.row, "dpq-futures", formatNumberToDisplay(item.getValue() as string)),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'dpq_futures')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_dpq_pct",
        header: "DPQ (Futures) %",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'dpq_futures')?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: (item) => generateCellLinkDetail(item.row, "dpq-futures", formatNumberToDisplay(item.getValue() as string, { suffix: "%" })),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'dpq_futures')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "futures_open_offers_qty",
        header: "Open Offers Qty",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'open_offer_id')?.value || 0,
        filterFn: "numberFilter" as any,
        cell: (item) => generateCellLinkDetail(item.row, "open_offers", formatNumberToDisplay(item.getValue() as string)),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'open_offer_id')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_open_offers_pct",
        header: "Open Offers %",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'open_offer_id')?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: (item) => generateCellLinkDetail(item.row, "open_offers", formatNumberToDisplay(item.getValue() as string, { suffix: "%" })),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'open_offer_id')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "futures_direct_sales_qty",
        header: "Direct Sales (Futures) Qty",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'oddities_futures')?.value || 0,
        filterFn: "numberFilter" as any,
        cell: (item) => generateCellLinkDetail(item.row, "direct-sales-non-appl", formatNumberToDisplay(item.getValue() as string)),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'oddities_futures')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_direct_sales_pct",
        header: "Direct Sales (Futures) %",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'oddities_futures')?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: (item) => generateCellLinkDetail(item.row, "direct-sales-non-appl", formatNumberToDisplay(item.getValue() as string, { suffix: "%" })),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'oddities_futures')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "futures_unprotected_qty",
        header: "Unprotected Qty",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'unprotected_future')?.value || 0,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(value)}
            </span>
          )
        },
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'unprotected_future')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "futures_unprotected_pct",
        header: "Unprotected %",
        accessorFn: (row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'unprotected_future')?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(value, { suffix: "%" })}
            </span>
          )
        },
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.futuresBreakdown.find((item: { id: string }) => item.id === 'unprotected_future')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "physical_basis_qty",
        header: "Basis Qty",
        accessorFn: (row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'basis_physical')?.value || 0,
        filterFn: "numberFilter" as any,
        cell: (item) => generateCellLinkDetail(item.row, "basis", formatNumberToDisplay(item.getValue() as string)),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'basis_physical')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "physical_basis_pct",
        header: "Basis %",
        accessorFn: (row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'basis_physical')?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: (item) => generateCellLinkDetail(item.row, "basis", formatNumberToDisplay(item.getValue() as string, { suffix: "%" })),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'basis_physical')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "physical_otc_qty",
        header: "OTC (Basis) Qty",
        accessorFn: (row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'otc_physical')?.value || 0,
        filterFn: "numberFilter" as any,
        cell: (item) => generateCellLinkDetail(item.row, "otc-beacon-basis", formatNumberToDisplay(item.getValue() as string)),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'otc_physical')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "physical_otc_pct",
        header: "OTC (Basis) %",
        accessorFn: (row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'otc_physical')?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: (item) => generateCellLinkDetail(item.row, "otc-beacon-basis", formatNumberToDisplay(item.getValue() as string, { suffix: "%" })),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'otc_physical')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "physical_dpq_qty",
        header: "DPQ (Basis) Qty",
        accessorFn: (row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'dpq_physical')?.value || 0,
        filterFn: "numberFilter" as any,
        cell: (item) => generateCellLinkDetail(item.row, "dpq-basis", formatNumberToDisplay(item.getValue() as string)),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'dpq_physical')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "physical_dpq_pct",
        header: "DPQ (Basis) %",
        accessorFn: (row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'dpq_physical')?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: (item) => generateCellLinkDetail(item.row, "dpq-basis", formatNumberToDisplay(item.getValue() as string, { suffix: "%" })),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'dpq_physical')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "oddities_physical_qty",
        header: "Direct Sales (Basis) Qty",
        accessorFn: (row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'oddities_physical')?.value || 0,
        filterFn: "numberFilter" as any,
        cell: (item) => generateCellLinkDetail(item.row, "direct-sales-basis", formatNumberToDisplay(item.getValue() as string)),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'oddities_physical')?.value || 0),

        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),

        enableFiltering: false,
      },
      {
        id: "oddities_physical_pct",
        header: "Direct Sales (Basis) %",
        accessorFn: (row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'oddities_physical')?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: (item) => generateCellLinkDetail(item.row, "direct-sales-basis", formatNumberToDisplay(item.getValue() as string, { suffix: "%" })),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'oddities_physical')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),

        enableFiltering: false,
      },
      {
        id: "physical_cash_qty",
        header: "Cash Qty",
        accessorFn: (row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'cash_physical')?.value || 0,
        filterFn: "numberFilter" as any,
        cell: (item) => generateCellLinkDetail(item.row, "cash", formatNumberToDisplay(item.getValue() as string)),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'cash_physical')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "physical_cash_pct",
        header: "Cash %",
        accessorFn: (row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'cash_physical')?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: (item) => generateCellLinkDetail(item.row, "cash", formatNumberToDisplay(item.getValue() as string, { suffix: "%" })),
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'cash_physical')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),

        enableFiltering: false,
      },
      {
        id: "physical_unprotected_qty",
        header: "Unsold Qty",
        accessorFn: (row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'unprotected_physical')?.value || 0,
        filterFn: "numberFilter" as any,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(value)}
            </span>
          )
        },
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'unprotected_physical')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["value"] as string),
        enableFiltering: false,
      },
      {
        id: "physical_unprotected_pct",
        header: "Unsold %",
        accessorFn: (row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'unprotected_physical')?.percent || 0,
        filterFn: "numberFilter" as any,
        sortingFn: percentSortingFn,
        cell: ({ getValue }) => {
          const value = getValue() as number;
          return (
            <span className="text-gray-600 text-sm">
              {formatNumberToDisplay(value, { suffix: "%" })}
            </span>
          )
        },
        aggregationFn: computeAggregatedWithFindValue((row: any) => row.physicalBreakdown.find((item: { id: string }) => item.id === 'unprotected_physical')?.value || 0),
        aggregatedCell: (item: any) => formatNumberToDisplay(item.getValue()?.["percent"] as string, { suffix: "%" }),
        enableFiltering: false,
      },
      {
        id: "client_id",
        header: "Agtech ID",
        aggregatedCell: ({ row, getValue }: any) => { return ""; },
        accessorFn: (row: any) => { return summaryData.customer_map?.[row.customer_id]?.client_id || ""; },
      },
      {
        id: "trader_id",
        accessorKey: "trader_id",
        header: "Trader",
        accessorFn: (row: any) => { return summaryData.customer_map?.[row.customer_id]?.Trader?.name || ""; },
      },
    ];

    return { isReady: true, customer_commodity_top_level_items, customer_commodity_top_level_columns };
  }, [summaryData]);

  const freightReportButton = ({ setGrouping, setColumnOrder, setColumnVisibility, grouping, columnOrder, columnVisibility }: any) => {
    return (
      <Button
        variant="outline"
        onClick={() => {
          setGrouping([]);
          setColumnOrder([
            "crop_year", "commodity_display_name", "customer_id", "acreage", "production", "percent_hedged", "percent_hedged_w_offer", "percent_physical",
            "futures_hta_qty", "futures_hta_pct", "futures_beacon_qty", "futures_beacon_pct", "futures_otc_qty", "futures_otc_pct",
"futures_brokerage_qty", "futures_brokerage_pct", "futures_dpq_qty", "futures_dpq_pct", "futures_open_offers_qty", "futures_open_offers_pct",
"futures_direct_sales_qty", "futures_direct_sales_pct", "futures_unprotected_qty", "futures_unprotected_pct", "physical_basis_qty", "physical_basis_pct",
"physical_otc_qty", "physical_otc_pct", "physical_beacon_qty", "physical_beacon_pct", "physical_dpq_qty", "physical_dpq_pct", "oddities_physical_qty", "oddities_physical_pct",
"physical_cash_qty", "physical_cash_pct", "physical_unprotected_qty", "physical_unprotected_pct", "client_id", "trader_id"
          ]);
          setColumnVisibility({ crop_year: true, commodity_display_name: true, customer_id: true });
        }}
      >
        Set Freight Report View
      </Button>
    );
  };

  return (
    <div className="gap-4 max-w-[calc(100vw-350px)] mt-4">
      <h3 className="text-lg font-bold">Risk Overview</h3>
      <DataTable
        tableId="CustomersTopLevelViewSummary"
        persistColumnSettings
        className="mx-4"
        tableClassName="max-h-[800px] overflow-y-auto"
        columns={customer_commodity_top_level_columns || []}
        data={customer_commodity_top_level_items || []}
        useSteppedGroupingExpansion
        defaultHideLeafRows
        defaultCondensed
        stickyHeaders
        enableColumnVisibility
        enableSorting
        enableMultiSort
        enableFiltering
        enableGrouping
        enableColumnResizing
        enableExport
        enableVirtualization
        enableColumnPinning
        enableMultiFilter
        enableMultiSelect
        virtualizationRowHeight={49}
        virtualizationOverscan={2}
        filterStyle='select'
        filterMode='internal'
        emptyMessage={!isReady ? "Loading Data" : "No data available"}
        groupBy={["commodity_display_name"]}
        defaultColumnVisibility={{ actual_production: false, projected_production: false, crop_year: false }}
        defaultSorting={[
          { id: "commodity_display_name", desc: false },
          { id: "customer_id", desc: false },
        ]}
        groupingBarButtons={[
          { label: "Group by Trader", id: 'risk_overview_group_by_trader', accessor_keys: ['trader_id', 'customer_id'] }
        ]}
        customToolbarButtons={[freightReportButton]}
      />
    </div>
  );
}
